import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area, 
  BarChart, 
  Bar,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { ShotData } from '../types';
import { TrendingUp, TrendingDown, Minus, BarChart3, Target } from 'lucide-react';
import { format, startOfWeek, startOfMonth, parseISO, isValid } from 'date-fns';

interface HistoricalTrendsProps {
  shots: ShotData[];
  className?: string;
}

type TimeGrouping = 'daily' | 'weekly' | 'monthly';
type ChartType = 'line' | 'area' | 'bar';

export const HistoricalTrends: React.FC<HistoricalTrendsProps> = ({ 
  shots, 
  className = '' 
}) => {
  const [timeGrouping, setTimeGrouping] = useState<TimeGrouping>('daily');
  const [chartType, setChartType] = useState<ChartType>('line');
  const [_selectedMetric, _setSelectedMetric] = useState<string>('all');
  
  // Process shots into time series data
  const timeSeriesData = useMemo(() => {
    const groupedData: Record<string, any> = {};
    
    shots.forEach(shot => {
      let date: Date;
      try {
        date = parseISO(shot.timestamp);
        if (!isValid(date)) {
          date = new Date(); // Fallback to current date if invalid
        }
      } catch {
        date = new Date(); // Fallback if parsing fails
      }
      
      let key: string;
      switch (timeGrouping) {
        case 'weekly':
          key = format(startOfWeek(date), 'yyyy-MM-dd');
          break;
        case 'monthly':
          key = format(startOfMonth(date), 'yyyy-MM');
          break;
        default: // daily
          key = format(date, 'yyyy-MM-dd');
      }
      
      if (!groupedData[key]) {
        groupedData[key] = {
          date: key,
          shots: 0,
          avgDistance: 0,
          avgQuality: 0,
          boundaries: 0,
          excellentShots: 0,
          totalDistance: 0,
          qualities: [],
        };
      }
      
      const group = groupedData[key];
      group.shots++;
      group.totalDistance += shot.distance;
      group.avgDistance = group.totalDistance / group.shots;
      
      // Track quality
      const qualityScore = shot.quality === 'excellent' ? 4 : 
                          shot.quality === 'good' ? 3 : 
                          shot.quality === 'fair' ? 2 : 1;
      group.qualities.push(qualityScore);
      group.avgQuality = group.qualities.reduce((a: number, b: number) => a + b, 0) / group.qualities.length;
      
      if (shot.quality === 'excellent') group.excellentShots++;
      if (shot.distance >= 60) group.boundaries++;
    });
    
    return Object.values(groupedData).sort((a: any, b: any) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  }, [shots, timeGrouping]);
  
  // Calculate shot type distribution
  const shotTypeDistribution = useMemo(() => {
    const distribution: Record<string, number> = {};
    
    shots.forEach(shot => {
      distribution[shot.shotType] = (distribution[shot.shotType] || 0) + 1;
    });
    
    return Object.entries(distribution)
      .map(([type, count]) => ({
        name: type,
        value: count,
        percentage: ((count / shots.length) * 100).toFixed(1)
      }))
      .sort((a, b) => b.value - a.value);
  }, [shots]);
  
  // Calculate performance metrics
  const performanceMetrics = useMemo(() => {
    if (shots.length === 0) return null;
    
    const totalShots = shots.length;
    const avgDistance = shots.reduce((sum, s) => sum + s.distance, 0) / totalShots;
    const avgPowerTransfer = shots.reduce((sum, s) => sum + s.powerTransfer, 0) / totalShots;
    const excellentPercentage = (shots.filter(s => s.quality === 'excellent').length / totalShots) * 100;
    const boundaryPercentage = (shots.filter(s => s.distance >= 60).length / totalShots) * 100;
    
    // Calculate trend (compare first half vs second half)
    const midPoint = Math.floor(totalShots / 2);
    const firstHalf = shots.slice(0, midPoint);
    const secondHalf = shots.slice(midPoint);
    
    const firstHalfAvg = firstHalf.reduce((sum, s) => sum + s.distance, 0) / firstHalf.length || 0;
    const secondHalfAvg = secondHalf.reduce((sum, s) => sum + s.distance, 0) / secondHalf.length || 0;
    const trend = secondHalfAvg > firstHalfAvg ? 'improving' : 
                  secondHalfAvg < firstHalfAvg ? 'declining' : 'stable';
    
    return {
      totalShots,
      avgDistance,
      avgPowerTransfer,
      excellentPercentage,
      boundaryPercentage,
      trend,
      improvement: ((secondHalfAvg - firstHalfAvg) / firstHalfAvg * 100).toFixed(1)
    };
  }, [shots]);
  
  // Colors for charts
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
  
  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving': return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'declining': return <TrendingDown className="h-4 w-4 text-red-600" />;
      default: return <Minus className="h-4 w-4 text-gray-600" />;
    }
  };
  
  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="font-semibold">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value.toFixed(1)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };
  
  return (
    <div className={`space-y-6 ${className}`}>
      {/* Performance Summary */}
      {performanceMetrics && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Shots</p>
                  <p className="text-2xl font-bold">{performanceMetrics.totalShots}</p>
                </div>
                <Target className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Avg Distance</p>
                  <p className="text-2xl font-bold">{performanceMetrics.avgDistance.toFixed(1)}m</p>
                </div>
                <BarChart3 className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div>
                <p className="text-sm text-gray-600">Excellent Shots</p>
                <p className="text-2xl font-bold">{performanceMetrics.excellentPercentage.toFixed(1)}%</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div>
                <p className="text-sm text-gray-600">Boundaries</p>
                <p className="text-2xl font-bold">{performanceMetrics.boundaryPercentage.toFixed(1)}%</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Trend</p>
                  <div className="flex items-center gap-2">
                    {getTrendIcon(performanceMetrics.trend)}
                    <span className="text-lg font-semibold capitalize">{performanceMetrics.trend}</span>
                  </div>
                </div>
                <Badge 
                  className={performanceMetrics.trend === 'improving' ? 'bg-green-100 text-green-800' : 
                            performanceMetrics.trend === 'declining' ? 'bg-red-100 text-red-800' : 
                            'bg-gray-100 text-gray-800'}
                >
                  {performanceMetrics.improvement}%
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Time Series Chart */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Performance Trends</CardTitle>
              <CardDescription>Shot metrics over time</CardDescription>
            </div>
            <div className="flex gap-2">
              <Select value={timeGrouping} onValueChange={(value) => setTimeGrouping(value as TimeGrouping)}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={chartType} onValueChange={(value) => setChartType(value as ChartType)}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="line">Line</SelectItem>
                  <SelectItem value="area">Area</SelectItem>
                  <SelectItem value="bar">Bar</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            {chartType === 'line' ? (
              <LineChart data={timeSeriesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="avgDistance" 
                  stroke="#3b82f6" 
                  name="Avg Distance (m)"
                  strokeWidth={2}
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="shots" 
                  stroke="#10b981" 
                  name="Shot Count"
                  strokeWidth={2}
                />
              </LineChart>
            ) : chartType === 'area' ? (
              <AreaChart data={timeSeriesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="avgDistance" 
                  stroke="#3b82f6" 
                  fill="#3b82f6"
                  fillOpacity={0.3}
                  name="Avg Distance (m)"
                />
                <Area 
                  type="monotone" 
                  dataKey="boundaries" 
                  stroke="#10b981" 
                  fill="#10b981"
                  fillOpacity={0.3}
                  name="Boundaries"
                />
              </AreaChart>
            ) : (
              <BarChart data={timeSeriesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="shots" fill="#3b82f6" name="Shots" />
                <Bar dataKey="excellentShots" fill="#10b981" name="Excellent" />
                <Bar dataKey="boundaries" fill="#f59e0b" name="Boundaries" />
              </BarChart>
            )}
          </ResponsiveContainer>
        </CardContent>
      </Card>
      
      {/* Shot Type Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Shot Type Distribution</CardTitle>
            <CardDescription>Breakdown by shot type</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={shotTypeDistribution.slice(0, 6)}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percentage }) => `${name}: ${percentage}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {shotTypeDistribution.slice(0, 6).map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        
        {/* Quality Trend */}
        <Card>
          <CardHeader>
            <CardTitle>Quality Trend</CardTitle>
            <CardDescription>Shot quality over time</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={timeSeriesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis domain={[0, 4]} ticks={[1, 2, 3, 4]} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="avgQuality" 
                  stroke="#8b5cf6" 
                  name="Avg Quality"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
      
      {/* Consistency Score */}
      <Card>
        <CardHeader>
          <CardTitle>Consistency Analysis</CardTitle>
          <CardDescription>Performance consistency metrics</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Distance Consistency</p>
                <div className="flex items-end gap-2">
                  <span className="text-2xl font-bold text-blue-600">
                    {shots.length > 0 ? 
                      (100 - (Math.sqrt(shots.reduce((sum, s) => 
                        sum + Math.pow(s.distance - performanceMetrics!.avgDistance, 2), 0
                      ) / shots.length) / performanceMetrics!.avgDistance * 100)).toFixed(1)
                      : 0}%
                  </span>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Power Consistency</p>
                <div className="flex items-end gap-2">
                  <span className="text-2xl font-bold text-green-600">
                    {shots.length > 0 ?
                      (100 - (Math.sqrt(shots.reduce((sum, s) => 
                        sum + Math.pow(s.powerTransfer - performanceMetrics!.avgPowerTransfer, 2), 0
                      ) / shots.length) / performanceMetrics!.avgPowerTransfer * 100)).toFixed(1)
                      : 0}%
                  </span>
                </div>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Quality Consistency</p>
                <div className="flex items-end gap-2">
                  <span className="text-2xl font-bold text-purple-600">
                    {performanceMetrics?.excellentPercentage.toFixed(1) || 0}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HistoricalTrends;